import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Clock,
  Globe,
  Check,
  X,
  Minus,
  Plus,
  ChevronDown,
  AlertCircle,
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import DropDownWithSearch from '../shared/DropDownWIthSearch';
import PlatformCalendar from './platformCalendar';
import { calendarOverrideStyles } from './datePickerStyles';
import ct from 'countries-and-timezones';
import './ModernScheduleInterview.css';

const getUTCOffset = (timeZone) => {
  if (!timeZone) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const offsetPart = parts.find((p) => p.type === 'timeZoneName');
    return offsetPart ? offsetPart.value.replace('GMT', 'UTC') : '';
  } catch (e) {
    return '';
  }
};

const parseDateSafely = (str) => {
  if (!str || typeof str !== 'string') return null;
  const cleanStr = str
    .replace(/(\d+)(st|nd|rd|th)/gi, '$1')
    .replace(/,/g, '')
    .trim();
  const parsed = new Date(cleanStr);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const ModernScheduleInterview = ({ onConfirm, onClose, isReschedule = false, existingStartDate, existingEndDate }) => {
  const { theme } = useTheme();
  const isRescheduleMode = isReschedule || !!(existingStartDate && existingEndDate && existingStartDate !== 'N/A' && existingEndDate !== 'N/A');

  const [selectedDay, setSelectedDay] = useState(() => {
    if (isRescheduleMode && existingStartDate && existingEndDate && existingStartDate !== 'N/A' && existingEndDate !== 'N/A') {
      const start = parseDateSafely(existingStartDate);
      const end = parseDateSafely(existingEndDate);
      if (start && end) {
        return null;
      }
    }
    return 1;
  });

  const [customDateRange, setCustomDateRange] = useState(() => {
    if (isRescheduleMode && existingStartDate && existingEndDate && existingStartDate !== 'N/A' && existingEndDate !== 'N/A') {
      const start = parseDateSafely(existingStartDate);
      const end = parseDateSafely(existingEndDate);
      if (start && end) {
        return [start, end];
      }
    }
    return [null, null];
  });

  const [isCustomCalendarOpen, setIsCustomCalendarOpen] = useState(false);
  const [isSpecificSlots, setIsSpecificSlots] = useState(false);
  const calendarRef = useRef(null);
  const hourBoxRef = useRef(null);
  const minuteBoxRef = useRef(null);
  const scheduleButtonRef = useRef(null);

  // Handle click outside to close calendar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCustomCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate default time based on current time
  const initialTime = useMemo(() => {
    const now = new Date();
    let h = now.getHours();
    let m = now.getMinutes();

    let roundedM;
    if (m === 0) roundedM = '00';
    else if (m <= 15) roundedM = '15';
    else if (m <= 30) roundedM = '30';
    else if (m <= 45) roundedM = '45';
    else {
      roundedM = '00';
      h = (h + 1) % 24;
    }

    const ampmVal = h >= 12 ? 'PM' : 'AM';
    let displayH = h % 12;
    displayH = displayH === 0 ? 12 : displayH;

    return {
      h: displayH.toString().padStart(2, '0'),
      m: roundedM,
      p: ampmVal,
    };
  }, []);

  // Helper to determine if a single date is currently targeted
  const isSingleDay = useMemo(() => {
    if (selectedDay === 1) return true;
    if (!selectedDay && customDateRange[0] && customDateRange[1]) {
      return (
        customDateRange[0].toDateString() === customDateRange[1].toDateString()
      );
    }
    return false;
  }, [selectedDay, customDateRange]);

  const isOriginalRange = useMemo(() => {
    if (!isRescheduleMode || !existingStartDate || !existingEndDate) return false;
    if (selectedDay !== null) return false;
    if (!customDateRange[0] || !customDateRange[1]) return false;

    const toDateOnlyStr = (dOrStr) => {
      let dateObj = null;
      if (dOrStr instanceof Date) {
        dateObj = dOrStr;
      } else if (typeof dOrStr === 'string' && dOrStr.trim() !== '') {
        dateObj = parseDateSafely(dOrStr);
      }

      if (dateObj) {
        const y = dateObj.getFullYear();
        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
        const d = String(dateObj.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
      return '';
    };

    const currentStartStr = toDateOnlyStr(customDateRange[0]);
    const currentEndStr = toDateOnlyStr(customDateRange[1]);
    const origStartStr = toDateOnlyStr(existingStartDate);
    const origEndStr = toDateOnlyStr(existingEndDate);

    return currentStartStr === origStartStr && currentEndStr === origEndStr;
  }, [isRescheduleMode, existingStartDate, existingEndDate, selectedDay, customDateRange]);

  const isDateInPast = useMemo(() => {
    if (selectedDay) {
      // Pills (Today, 3 Days, etc.) always start from today, so they are never in the past
      return false;
    }
    if (!customDateRange[0]) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const checkDate = new Date(customDateRange[0]);
    checkDate.setHours(0, 0, 0, 0);

    return checkDate < today;
  }, [selectedDay, customDateRange]);

  // Specific slots logic: Only show toggle section if 1 day is selected.
  const showToggleSection = isSingleDay;

  // Time Picker State
  const [hour, setHour] = useState(initialTime.h);
  const [minute, setMinute] = useState(initialTime.m);
  const [ampm, setAmpm] = useState(initialTime.p);
  const [hourError, setHourError] = useState(false);
  const [minuteError, setMinuteError] = useState(false);

  // Restore HH/MM defaults if user clears them and clicks outside the field
  // (but not when clicking the Schedule button — that path must trigger validation)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scheduleButtonRef.current && scheduleButtonRef.current.contains(event.target)) {
        return;
      }
      if (hourBoxRef.current && !hourBoxRef.current.contains(event.target) && !hour) {
        setHour(initialTime.h);
        setHourError(false);
      }
      if (minuteBoxRef.current && !minuteBoxRef.current.contains(event.target) && !minute) {
        setMinute(initialTime.m);
        setMinuteError(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [hour, minute, initialTime]);

  const hourOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const h = (i + 1).toString().padStart(2, '0');
        return { label: h, value: h };
      }),
    [],
  );

  const minuteOptions = useMemo(
    () => ['00', '15', '30', '45'].map((m) => ({ label: m, value: m })),
    [],
  );

  const daysOptions = [
    { label: 'Today', value: 1 },
    { label: '3 Days', value: 3 },
    { label: '5 Days', value: 5 },
    { label: '10 Days', value: 10 },
    { label: '15 Days', value: 15 },
  ];

  const dropdownData = useMemo(() => {
    return Object.values(ct.getAllTimezones()).map((tz) => {
      const offset = getUTCOffset(tz.name);
      return {
        label: `${tz.name} (${offset})`,
        value: tz.name,
      };
    });
  }, []);

  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    const browserTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tz = ct.getTimezone(browserTZ);
    return tz
      ? {
          label: `${tz.name} (${getUTCOffset(tz.name)})`,
          value: tz.name,
        }
      : null;
  });
  // Tracks whether the user explicitly changed the timezone from the default
  const [timezoneExplicitlySelected, setTimezoneExplicitlySelected] =
    useState(false);

  const isPastSelected = useMemo(() => {
    if (!showToggleSection || !isSpecificSlots) return false;

    const resolvedTimezone =
      selectedTimezone?.value ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    let h = parseInt(hour, 10);
    if (ampm === 'PM' && h !== 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;

    const baseDate = !selectedDay && customDateRange[0] ? new Date(customDateRange[0]) : new Date();
    
    const tz = ct.getTimezone(resolvedTimezone);
    const offset = tz ? tz.utcOffset : 0;

    const dateAsUTC = new Date(Date.UTC(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      baseDate.getDate(),
      h,
      parseInt(minute, 10),
      0,
      0
    ));

    const targetAbsoluteDate = new Date(dateAsUTC.getTime() - (offset * 60 * 1000));

    return targetAbsoluteDate < new Date();
  }, [showToggleSection, isSpecificSlots, selectedDay, customDateRange, hour, minute, ampm, selectedTimezone]);

  const isDateRangeSelected = selectedDay !== null || (customDateRange[0] && customDateRange[1]);

  const displayDateText = useMemo(() => {
    const formatDate = (d) => {
      if (!d) return '';
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    };

    if (!selectedDay) {
      if (customDateRange[0] && customDateRange[1]) {
        if (
          customDateRange[0].toDateString() ===
          customDateRange[1].toDateString()
        ) {
          return `${formatDate(customDateRange[0])}`;
        }
        return `${formatDate(customDateRange[0])} to ${formatDate(customDateRange[1])}`;
      }
      return '...';
    }

    const start = new Date();
    const end = new Date();
    const daysToAdd = selectedDay || 1;
    end.setDate(start.getDate() + (daysToAdd - 1));

    if (start.toDateString() === end.toDateString()) {
      return `${formatDate(start)}`;
    }

    return `${formatDate(start)} to ${formatDate(end)}`;
  }, [selectedDay, customDateRange]);

  const handleConfirm = () => {
    if (onConfirm) {
      const formattedTime = `${hour}:${minute} ${ampm}`;
      const finalDays =
        !selectedDay && customDateRange[0] && customDateRange[1]
          ? Math.ceil(
              Math.abs(customDateRange[1] - customDateRange[0]) /
                (1000 * 60 * 60 * 24),
            ) + 1 // Add 1 to be inclusive
          : selectedDay || 1;

      const isSpecific = isSingleDay && isSpecificSlots;

      if (isSpecific) {
        const hErr = !hour;
        const mErr = !minute;
        setHourError(hErr);
        setMinuteError(mErr);
        if (hErr || mErr) return;
      }

      // Always send a valid timezone: user-selected OR browser/system fallback
      const resolvedTimezone =
        selectedTimezone?.value ||
        Intl.DateTimeFormat().resolvedOptions().timeZone;

      let finalStartDate;
      let finalEndDate;

      if (isSpecific) {
        // Calculate specific start time based on selected timezone
        const parseTime = (h, m, p) => {
          let hours = parseInt(h, 10);
          if (p === 'PM' && hours !== 12) hours += 12;
          if (p === 'AM' && hours === 12) hours = 0;
          return { hours, minutes: parseInt(m, 10) };
        };

        const timeParts = parseTime(hour, minute, ampm);
        const baseDate = (!selectedDay && customDateRange[0]) ? customDateRange[0] : new Date();

        const year = baseDate.getFullYear();
        const month = baseDate.getMonth();
        const day = baseDate.getDate();

        // Get timezone offset in minutes
        const tz = ct.getTimezone(resolvedTimezone);
        const offset = tz ? tz.utcOffset : 0;

        // Create Date object interpreted as UTC Selection Time
        const dateAsUTC = new Date(Date.UTC(year, month, day, timeParts.hours, timeParts.minutes));
        // Adjust to absolute UTC: UTC_Time = Selection_Time - Offset
        finalStartDate = new Date(dateAsUTC.getTime() - (offset * 60 * 1000));
        // End date is 24 hours later for specific slots
        finalEndDate = new Date(finalStartDate.getTime() + (24 * 60 * 60 * 1000));
      } else {
        // Range Selection Mode
        if (!selectedDay) {
          finalStartDate = new Date(customDateRange[0]);
          finalStartDate.setHours(0, 0, 0, 0);
          
          finalEndDate = new Date(customDateRange[1]);
          // Set to the end of the day to give the candidate the full day
          finalEndDate.setHours(23, 59, 59, 0);
        } else {
          finalStartDate = new Date();
          finalEndDate = new Date(finalStartDate);
          finalEndDate.setDate(finalStartDate.getDate() + finalDays);
        }
      }

      onConfirm({
        selectedDay: finalDays,
        startDate: finalStartDate,
        endDate: finalEndDate,
        isSpecificSlots: isSpecific,
        selectedTime: isSpecific ? formattedTime : null,
        timezone: resolvedTimezone,
        timezoneExplicitlySelected,
        timeslot: isSpecific ? formattedTime : null,
      });
    }
  };

  return (
    <div
      data-guide="schedule-modal"
      className={`modern-schedule-container ${theme} !p-10`}
      style={{
        position: 'relative',
        minHeight: showToggleSection && isSpecificSlots ? '620px' : '440px',
        transition: 'min-height 0.3s ease-in-out',
      }}
    >
      <style>{calendarOverrideStyles(theme)}</style>
      <style>{`
        /* Hide browser-default arrows in number inputs */
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
        /* Force dropdown list to open upwards */
        .dropdown-up > div:last-child {
          top: auto !important;
          bottom: 100% !important;
          margin-bottom: 4px !important;
        }

        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }

          15%, 35%, 55%, 75%, 95% {
            transform: translateX(-2px);
          }

          10%, 30%, 50%, 70%, 90% {
            transform: translateX(2px);
          }
        }

        .error-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-10 right-10 p-1 hover:opacity-70 transition-opacity"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
          }}
          title="Close"
        >
          <X size={20} />
        </button>
      )}

      <h2 className={`modern-schedule-title !mb-3 !mt-0 !font-medium ${theme !== 'dark' ? '!text-[#404040]' : ''}`}>
        {isRescheduleMode ? 'Reschedule Interview' : 'Schedule Interview'}
      </h2>

      <div className="section-label-group !mb-2">
        <h3 className={`section-title !font-normal ${theme !== 'dark' ? '!text-[#404040]' : ''}`}>Select availability window</h3>
        <p className={`section-subtitle ${theme !== 'dark' ? '!text-[#404040]' : ''}`}>
          Interview can be scheduled within this period
        </p>

        <div className="day-pills-container flex flex-wrap items-center gap-2">
          {daysOptions.map((option) => (
            <button
              key={option.value}
              className={`${theme === 'dark' ? 'button' : 'border-btn !text-[#404040]'} ${selectedDay === option.value ? 'selected-slot' : ''} px-4 py-2 rounded-full text-sm font-medium w-fit`}
              onClick={() => {
                setSelectedDay(option.value);
                setCustomDateRange([null, null]);
              }}
            >
              <span className="whitespace-nowrap">{option.label}</span>
            </button>
          ))}
          <div className="flex items-center gap-2">
            <div className="relative w-fit" ref={calendarRef}>
              <button
                className={`${theme === 'dark' ? 'button' : 'border-btn !text-textPrimary'} ${!selectedDay && customDateRange[0] ? 'selected-slot' : ''} flex items-center justify-between w-full rounded-full px-4 py-2 text-sm font-medium h-10`}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCustomCalendarOpen(!isCustomCalendarOpen);
                }}
                aria-expanded={isCustomCalendarOpen}
              >
                <span className="truncate">
                  {customDateRange[0] && customDateRange[1]
                    ? `${customDateRange[0].toLocaleDateString()} - ${customDateRange[1].toLocaleDateString()}`
                    : 'Select Date Range'}
                </span>
                {customDateRange[0] && customDateRange[1] ? (
                  <X
                    className="w-4 h-4 ml-2 flex-shrink-0 cursor-pointer relative z-[100]"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setCustomDateRange([null, null]);
                      setIsCustomCalendarOpen(false);
                      setSelectedDay(1); // Default back to Today
                    }}
                  />
                ) : (
                  <ChevronDown
                    className="w-4 h-4 ml-2 flex-shrink-0 dark:!text-[#ffffff] z-[99]"
                    style={{ color: theme === 'dark' ? '#ffffff !important' : '#6b7280' }}
                  />
                )}
              </button>
              {isCustomCalendarOpen && (
                <div
                  className="candidate-analytics-calendar absolute mt-2 z-[100] right-0"
                  style={{
                    background: theme === 'dark' ? '#0a1733' : '#fff',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    transform: 'scale(0.75)',
                    transformOrigin: 'top right',
                    right: '-60px',
                  }}
                >
                  <div className={`platform-calendar ${theme}`}>
                    <PlatformCalendar
                      onChange={(value) => {
                        setCustomDateRange(value);
                        if (value[0] && value[1]) {
                          setIsCustomCalendarOpen(false);
                          setSelectedDay(null); // Deselect pills
                        }
                      }}
                      value={customDateRange}
                      selectRange={true}
                      minDate={new Date()}
                      maxDate={(() => {
                        const d = new Date();
                        d.setMonth(d.getMonth() + 6);
                        return d;
                      })()}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
  className="info-card !mb-0 !mt-0 items-start flex"
  style={{ background: theme === 'dark' ? '' : 'linear-gradient(to right, rgba(255, 96, 182, 0.1), rgba(41, 96, 253, 0.1))' }}
>
  <div className="info-icon-box mt-1">
    <Clock size={20} className="text-gray-400" />
  </div>

  <div className="info-content flex-1">
    <div className="flex items-center gap-2">
      <h4
        className={`leading-tight ${theme !== 'dark' ? '!text-[#404040]' : ''}`}
        style={{ marginBottom: '4px', color: theme === 'dark' ? '' : '#404040', whiteSpace: 'nowrap' }}
      >
        {showToggleSection
          ? 'Preferred time slot for the selected day'
          : 'Full-day candidate availability'}
      </h4>

      <span
        className="optional-tag ml-3"
        style={{ visibility: showToggleSection ? 'visible' : 'hidden', flexShrink: 0 }}
      >
        Optional
      </span>
    </div>

    <p
      className={`leading-snug ${theme !== 'dark' ? '!text-[#404040]' : ''}`}
      style={{ marginTop: '0px', color: theme === 'dark' ? '' : '#404040', whiteSpace: 'nowrap' }}
    >
      {showToggleSection
        ? 'Add preferred interview timing for the candidate.'
        : 'Candidate can attend the interviews anytime within the selected days.'}
    </p>
  </div>

  {showToggleSection && (
    <div className="ml-4 flex items-center justify-end">
      <label className="switch">
        <input
          type="checkbox"
          checked={isSpecificSlots}
          onChange={(e) => setIsSpecificSlots(e.target.checked)}
        />
        <span className="slider round"></span>
      </label>
    </div>
  )}
</div>

      <div
        className="advanced-settings"
        style={{
          marginTop: '0',
          maxHeight: showToggleSection && isSpecificSlots ? '400px' : '0px',
          overflow: showToggleSection && isSpecificSlots ? 'visible' : 'hidden',
          opacity: showToggleSection && isSpecificSlots ? 1 : 0,
          transition: 'max-height 0.2s ease, opacity 0.15s ease',    
        }}
      >
        {/* <p className="text-[11px] text-gray-400 mt-1 pl-2">
                {selectedTimezone ? getUTCOffset(selectedTimezone.value) : ''}
              </p> */}

        <div className="time-picker-container !mt-3">
          <span className="time-picker-label !mb-1">Timezone</span>
          <DropDownWithSearch
            dropdownData={dropdownData}
            onSelect={(item) => {
              setSelectedTimezone(item);
              setTimezoneExplicitlySelected(true);
            }}
            placeholder="Select Timezone"
            selectedValue={selectedTimezone}
            showClearButton={false}
            specialClasses="h-10 !text-sm z-[100]"
          />
        </div>

        <div className="time-picker-container !mt-4">
          <span className="time-picker-label !mb-1">Select Time</span>
          <div className="time-picker-boxes !flex !items-center !gap-2">
            <div className="time-box-group !w-[100px] relative" ref={hourBoxRef}>
              <DropDownWithSearch
                dropdownData={hourOptions}
                onSelect={(item) => { setHour(item.value); setHourError(false); }}
                onInputChange={(val) => { setHour(val); if (val) setHourError(false); }}
                placeholder="HH"
                selectedValue={{ label: hour, value: hour }}
                showClearButton={false}
                specialClasses={`h-10 !pr-10 !text-sm${hourError ? ' !border-red-500' : ''}`}
                disableSort={true}
                onlyWholeNumbers={true}
                maxLength={2}
                maxNumericValue={12}
              />
              <span className="absolute right-8 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 pointer-events-none uppercase">
                HH
              </span>
            </div>

            <span className="time-separator !mb-0 !leading-none mt-[-4px]">
              :
            </span>

            <div className="time-box-group !w-[110px] relative" ref={minuteBoxRef}>
              <DropDownWithSearch
                dropdownData={minuteOptions}
                onSelect={(item) => { setMinute(item.value); setMinuteError(false); }}
                onInputChange={(val) => { setMinute(val); if (val) setMinuteError(false); }}
                placeholder="MM"
                selectedValue={{ label: minute, value: minute }}
                showClearButton={false}
                specialClasses={`h-10 !pr-10 !text-sm${minuteError ? ' !border-red-500' : ''}`}
                disableSort={true}
                onlyWholeNumbers={true}
                maxLength={2}
                maxNumericValue={59}
              />
              <span className="absolute right-8 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 pointer-events-none uppercase">
                MM
              </span>
            </div>

            <div className="ampm-toggle !flex !flex-row !items-center !h-[36px] !p-1 !mb-0 ml-2">
              <button
                className={`ampm-btn !h-full ${ampm === 'AM' ? 'active' : ''}`}
                onClick={() => setAmpm('AM')}
              >
                AM
              </button>
              <button
                className={`ampm-btn !h-full ${ampm === 'PM' ? 'active' : ''}`}
                onClick={() => setAmpm('PM')}
              >
                PM
              </button>
            </div>
          </div>
        </div>
      </div>

      {isRescheduleMode && isOriginalRange ? (
        isDateInPast ? (
          <div
            className="flex items-center gap-2 px-4 py-2 my-2 mt-8 rounded-lg border text-[13px] font-medium transition-colors duration-200 w-full"
            style={{
              backgroundColor: theme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.15)',
              borderColor: theme === 'dark' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.3)',
              color: theme === 'dark' ? '#f87171' : '#b91c1c',
              minHeight: '40px',
            }}
          >
            <AlertCircle size={15} className="flex-shrink-0" />
            <span>The current scheduled date is in the past. Please select a future date to reschedule.</span>
          </div>
        ) : (
          <div
            className="flex items-center gap-2 px-4 py-2 my-2 mt-8 rounded-lg border text-[13px] font-medium transition-colors duration-200 w-full"
            style={{
              backgroundColor: theme === 'dark' ? 'rgba(234, 179, 8, 0.1)' : 'rgba(234, 179, 8, 0.15)',
              borderColor: theme === 'dark' ? 'rgba(234, 179, 8, 0.2)' : 'rgba(234, 179, 8, 0.3)',
              color: theme === 'dark' ? '#fbbf24' : '#b45309',
              minHeight: '40px',
            }}
          >
            <AlertCircle size={15} className="flex-shrink-0" />
            <span>This candidate's interview is already scheduled for <span className="font-bold">{displayDateText}</span></span>
          </div>
        )
      ) : (
        <div
          className={`flex items-center gap-2 px-4 py-2 my-2 mt-8 rounded-lg border text-[13px] font-medium transition-colors duration-200 w-full ${isPastSelected ? 'error-shake' : ''}`}
          style={{
            backgroundColor: 'transparent',
            borderColor: isPastSelected ? '#ef4444' : (theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#F4F4F5'),
            color: isPastSelected ? '#ef4444' : (theme === 'dark' ? '#A1A1AA' : '#52525B'),
            minHeight: '40px',
          }}
        >
          <AlertCircle size={15} className="flex-shrink-0" />
          {isPastSelected ? (
            <span>Selected time is already past, please select another time.</span>
          ) : (
            <span>
              {isRescheduleMode ? 'New interview will be scheduled for the ' : 'Interview will be scheduled for the '}
              <span className="font-bold">{displayDateText}</span>
              {showToggleSection && isSpecificSlots && (
                <span className="font-bold"> ({hour}:{minute} {ampm}, {selectedTimezone?.value || Intl.DateTimeFormat().resolvedOptions().timeZone})</span>
              )}
            </span>
          )}
        </div>
      )}

      <div className="flex justify-end mt-8 w-full">
        <button
          ref={scheduleButtonRef}
          className={`px-6 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-sm button ${(isPastSelected || (isRescheduleMode && isOriginalRange && isDateInPast)) ? '!opacity-50 !cursor-not-allowed' : ''}`}
          onClick={handleConfirm}
          disabled={isPastSelected || !isDateRangeSelected || (isRescheduleMode && isOriginalRange && isDateInPast)}
        >
          <span>{isRescheduleMode ? 'Reschedule Interview' : 'Schedule Interview'}</span>
        </button>
      </div>
    </div>
  );
};

export default ModernScheduleInterview;
